/**
 * Exécution d'une opération sur la cible choisie (store.executionTarget) :
 * route locale directe ou job distribué vers la machine pairée.
 */
import { APIClient } from "../api/client.js";
import { toast } from "../components/Toast.js";
import { type CurrentResult, store } from "../state/store.js";
import type { GenerationJob, GenerationJobKind } from "../types/index.js";
import {
	HostedJobFailure,
	type HostedJobResult,
	firstArtifactUrl,
	routeToOwnMachine,
	waitForHostedResult,
} from "./hosted-jobs.js";
import { forgetPendingJob, readPendingJob, rememberPendingJob } from "./pending-job.js";

/**
 * Whether the server this page came from can run a generation itself.
 *
 * Only an explicit `true` counts: a hosted API reports `false`, and before the
 * first `/api/status` answer nothing is known -- sending a job to local ML
 * routes on a guess is how the public site ended up posting to a 404.
 */
export function serverGenerates(): boolean {
	return store.get().status?.generates_here === true;
}

export interface RunOperationOptions {
	kind: GenerationJobKind;
	payload: Record<string, unknown>;
	runLocal: () => Promise<void>;
	handleHosted: (result: HostedJobResult, job: GenerationJob) => Promise<void> | void;
	hostedFailureMessage: string;
}

/**
 * Unique local↔paired-machine routing boundary used by generation workspaces
 * and Tools. Hosted pages never call local ML routes directly.
 */
export async function runOperation(options: RunOperationOptions): Promise<boolean> {
	const target = store.get().executionTarget;
	if (target.kind === "local") {
		// The hosted site has no GPU and no local ML routes: /api/generate and
		// friends answer 404 there. The redesign defaulted every workspace to
		// "local" regardless, so on the public site no button could generate --
		// the very defect `generates_here` was added to the old interface for.
		if (!serverGenerates()) {
			toast(
				"Ce site ne génère pas lui-même : connectez-vous, lancez le programme sur votre " +
					"ordinateur (page Compte), puis choisissez-le dans « Générer sur ».",
				"warning"
			);
			return false;
		}
		return runHere(options);
	}

	const workerId = target.worker.worker_id;
	toast(`Envoi à ${target.worker.gpu_name ?? "votre ordinateur"}…`, "info");
	store.setState({
		run: { message: "Envoi à votre ordinateur…", percent: null, machineGone: false },
	});
	let job: GenerationJob;
	try {
		job = await APIClient.submitHostedJob(
			options.payload,
			routeToOwnMachine(workerId),
			options.kind
		);
	} catch (error) {
		reportJobFailure(error, options.kind, options.hostedFailureMessage);
		store.setState({ run: null });
		return false;
	}
	// Écrit avant la première attente : ce qui suit peut ne jamais rendre la
	// main (onglet gelé puis jeté par iOS), et c'est précisément le cas où la
	// page doit pouvoir retrouver ce job toute seule au prochain chargement.
	rememberPendingJob({ job_id: job.job_id, kind: options.kind, worker_id: workerId });
	return followHostedJob(job, options, workerId);
}

/**
 * Générer sur la machine qui sert la page.
 *
 * `/api/generate` est synchrone : il ne rend la main qu'avec l'image. Le
 * pourcentage, lui, arrive par le sondage central de `/api/status` — c'est la
 * seule route qui le porte en local. On le recopie dans `store.run` le temps
 * de la génération, pour que le bouton, la barre du haut et le panneau de
 * résultat lisent la même chose ici que pour une machine pairée.
 */
async function runHere(options: Pick<RunOperationOptions, "runLocal">): Promise<boolean> {
	store.setState({
		run: { message: "Génération sur cet ordinateur…", percent: null, machineGone: false },
	});
	const stopFollowing = store.subscribe(
		(s) => s.status?.generation_progress ?? -1,
		(percent) => {
			const run = store.get().run;
			// Une génération terminée dont le dernier sondage arrive en retard
			// ne doit pas ressusciter une barre de progression.
			if (!run || percent < 0) return;
			store.setState({ run: { ...run, percent } });
		}
	);
	try {
		await options.runLocal();
		return true;
	} finally {
		stopFollowing();
		store.setState({ run: null });
	}
}

/**
 * Suivre un job hébergé jusqu'à son terme, et publier ce qu'il a produit.
 *
 * Une seule voie, empruntée aussi bien juste après l'envoi qu'au rechargement
 * d'une page qui avait laissé un job en route.
 */
async function followHostedJob(
	job: GenerationJob,
	options: Pick<RunOperationOptions, "kind" | "handleHosted" | "hostedFailureMessage">,
	workerId: string | null | undefined
): Promise<boolean> {
	// La phrase déjà annoncée. Le rappel ci-dessous est appelé à chaque
	// interrogation du serveur -- toutes les 2,5 s puis 5 s -- et il créait un
	// popup neuf à chaque fois, avec la même phrase : une quinzaine pour les
	// 86 s de « Vérification du modèle », autant pour « Initialisation », et
	// ainsi de suite jusqu'au premier pourcentage. Rapporté par le
	// propriétaire le 2026-09-28 sur son téléphone, où l'écran finit couvert.
	// La phrase reste affichée en permanence dans le panneau de résultat
	// (`store.run`) ; le popup ne sert qu'à annoncer un changement.
	let announced: string | null = null;
	try {
		const result = await waitForHostedResult(
			job,
			(progress) => {
				// Le pourcentage était calculé puis jeté : seule une phase de
				// préparation produisait un toast, qui s'efface au bout de
				// quelques secondes. Il va maintenant à l'écran, là où le
				// résultat apparaîtra.
				const gone = workerId ? !machineStillOnline(workerId) : false;
				// Une machine partie ne « prépare » plus rien : répéter sa
				// dernière phrase toutes les cinq secondes contredit le bloc
				// qui vient de dire qu'elle ne répond plus.
				if (progress.preparing && !gone && progress.message !== announced) {
					announced = progress.message;
					toast(progress.message, "info");
				}
				store.setState({
					run: {
						message: progress.preparing ? progress.message : "Génération sur votre ordinateur…",
						percent: Number.isFinite(progress.percent) ? progress.percent : null,
						// Une machine éteinte en plein travail continuait d'afficher
						// « Préparation… » : le dernier événement reçu ne change plus,
						// donc l'écran rassure alors que plus personne ne travaille.
						machineGone: gone,
					},
				});
			},
			options.hostedFailureMessage
		);
		await options.handleHosted(result, job);
		return true;
	} catch (error) {
		reportJobFailure(error, options.kind, options.hostedFailureMessage);
		return false;
	} finally {
		store.setState({ run: null });
		// Terminé, dans un sens ou dans l'autre : plus rien à reprendre.
		forgetPendingJob();
	}
}

/** L'échec, là où l'image serait apparue.
 *
 * Un toast vit 4,5 secondes. Une génération ratée pendant qu'on regardait
 * ailleurs ne laissait alors aucune trace : le bouton redevenait « Générer »
 * et l'écran était identique à celui d'avant. */
function reportJobFailure(error: unknown, kind: GenerationJobKind, fallback: string): void {
	const message = error instanceof Error ? error.message : fallback;
	toast(message, "error");
	store.setState({
		currentResult: {
			kind: resultKindOf(kind),
			url: "",
			filename: "",
			error: message,
			jobId: error instanceof HostedJobFailure ? error.jobId : null,
		},
	});
}

function resultKindOf(kind: GenerationJobKind): CurrentResult["kind"] {
	return kind === "video" ? "video" : kind === "audio" ? "audio" : "image";
}

/** Whether the machine we handed this job to is still reporting in.
 *
 * `store.workers` is refreshed every 5 s by the Account workspace, whichever
 * workspace is on screen -- the same source the estimate line already uses to
 * fall back to "Estimation disponible une fois votre machine connectée". */
function machineStillOnline(workerId: string): boolean {
	const worker = store.get().workers.find((candidate) => candidate.worker_id === workerId);
	// Unknown is not "gone": a refresh that failed must not raise a false alarm.
	return worker === undefined || worker.online !== false;
}

export interface RunOptions {
	kind: "image" | "video" | "audio";
	/** Payload envoyé à la machine (sans optimization_options : chaque
	 * machine applique ses propres réglages mémoire). */
	payload: Record<string, unknown>;
	runLocal: () => Promise<void>;
	hostedFailureMessage: string;
}

/** Publier l'artefact d'un job terminé, là où le résultat s'affiche. */
function publishJobArtifact(
	result: HostedJobResult,
	job: GenerationJob,
	jobKind: GenerationJobKind
): void {
	const url = firstArtifactUrl(result);
	if (!url) throw new Error("Le job s'est terminé sans artefact.");
	const kind = resultKindOf(jobKind);
	const currentResult: CurrentResult = {
		kind,
		url,
		filename: `job-${job.job_id}.${kind === "image" ? "png" : kind === "video" ? "mp4" : "wav"}`,
	};
	store.setState({
		currentResult,
		historyVersion: store.get().historyVersion + 1,
	});
}

/** Generation-specific wrapper preserving the existing result publication. */
export async function runGeneration(options: RunOptions): Promise<boolean> {
	return runOperation({
		...options,
		handleHosted: (result, job) => publishJobArtifact(result, job, options.kind),
	});
}

/**
 * Reprendre le job qu'une page précédente avait laissé en route.
 *
 * Appelé une fois au démarrage. Le serveur est la vérité : s'il a fini
 * pendant l'absence, l'image s'affiche ; s'il travaille encore, la page se
 * rebranche sur son journal d'événements, qui reconstruit la progression
 * depuis le début (`waitForHostedResult` repart du curseur zéro).
 *
 * Sans jeton, sans job noté, ou si le serveur ne le connaît plus, il ne se
 * passe rien : une page qui démarre ne doit jamais échouer à cause de ça.
 */
export async function resumePendingJob(): Promise<void> {
	const pending = readPendingJob();
	if (!pending || !APIClient.hasAuthToken()) return;

	let job: GenerationJob;
	try {
		job = await APIClient.getJob(pending.job_id);
	} catch (error) {
		// 401/403/404 : la session a changé, ou ce job n'existe plus — on
		// oublie. Une panne réseau passagère, elle, ne doit pas effacer la
		// seule trace d'une génération en cours.
		const status = (error as { status?: number }).status;
		if (status === 401 || status === 403 || status === 404) forgetPendingJob();
		return;
	}

	const failure = "La génération lancée précédemment n'a pas abouti.";
	if (job.status === "succeeded") {
		try {
			publishJobArtifact((job.result || {}) as HostedJobResult, job, pending.kind);
		} catch (error) {
			reportJobFailure(error, pending.kind, failure);
		}
		forgetPendingJob();
		return;
	}
	if (job.status === "failed" || job.status === "cancelled") {
		const result = (job.result || {}) as HostedJobResult;
		reportJobFailure(new Error(result.error || failure), pending.kind, failure);
		forgetPendingJob();
		return;
	}

	store.setState({
		run: {
			message: "Reprise de la génération en cours…",
			percent: null,
			machineGone: false,
		},
	});
	await followHostedJob(
		job,
		{
			kind: pending.kind,
			hostedFailureMessage: failure,
			handleHosted: (result, finished) => publishJobArtifact(result, finished, pending.kind),
		},
		pending.worker_id
	);
}
